import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DRIVER_STATUSES = {
  idle: { color: '#38a169', label: 'Idle', bg: 'bg-success', text: 'text-success' },
  active: { color: '#2563eb', label: 'Active', bg: 'bg-blue-500', text: 'text-blue-500' },
  en_route: { color: '#d97706', label: 'En Route', bg: 'bg-warning', text: 'text-warning' },
  offline: { color: '#718096', label: 'Offline', bg: 'bg-gray-400', text: 'text-gray-400' },
};

const MOCK_POSITIONS = [
  { x: 20, y: 30 }, { x: 45, y: 20 }, { x: 65, y: 45 }, { x: 30, y: 60 },
  { x: 75, y: 25 }, { x: 55, y: 70 }, { x: 15, y: 55 }, { x: 80, y: 60 },
];

const FleetMapPanel = ({ drivers = [], onDriverSelect, selectedDriver }) => {
  const [mapZone, setMapZone] = useState('all');
  const [hoveredDriver, setHoveredDriver] = useState(null);

  const zones = ['all', 'CBD', 'Westlands', 'Eastlands', 'Southlands'];

  const filteredDrivers = mapZone === 'all' ? drivers : drivers?.filter(d => d?.zone === mapZone);

  return (
    <div className="flex flex-col h-full bg-surface rounded-xl border border-border overflow-hidden">
      {/* Map Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary">
        <div className="flex items-center space-x-2">
          <Icon name="Map" size={18} color="#ffd700" />
          <span className="text-sm font-semibold text-white">Live Fleet Map</span>
          <span className="px-2 py-0.5 bg-accent text-primary text-xs font-bold rounded-full">
            {drivers?.filter(d => d?.status !== 'offline')?.length} Active
          </span>
        </div>
        <div className="flex items-center space-x-1">
          {zones?.map(zone => (
            <button
              key={zone}
              onClick={() => setMapZone(zone)}
              className={`px-2 py-1 text-xs rounded-md font-medium transition-colors duration-200 ${
                mapZone === zone
                  ? 'bg-accent text-primary' :'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {zone === 'all' ? 'All Zones' : zone}
            </button>
          ))}
        </div>
      </div>
      {/* Map Canvas */}
      <div className="relative flex-1 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 overflow-hidden min-h-64">
        {/* Grid lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#ffd700" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Road lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="40%" x2="100%" y2="40%" stroke="#94a3b8" strokeWidth="2" strokeDasharray="8,4"/>
          <line x1="0" y1="70%" x2="100%" y2="70%" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="8,4"/>
          <line x1="30%" y1="0" x2="30%" y2="100%" stroke="#94a3b8" strokeWidth="2" strokeDasharray="8,4"/>
          <line x1="65%" y1="0" x2="65%" y2="100%" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="8,4"/>
          <line x1="10%" y1="20%" x2="50%" y2="60%" stroke="#94a3b8" strokeWidth="1" strokeDasharray="6,4"/>
          <line x1="50%" y1="10%" x2="90%" y2="80%" stroke="#94a3b8" strokeWidth="1" strokeDasharray="6,4"/>
        </svg>

        {/* Zone labels */}
        <div className="absolute top-3 left-3 text-xs text-white/30 font-mono">CBD</div>
        <div className="absolute top-3 right-8 text-xs text-white/30 font-mono">Westlands</div>
        <div className="absolute bottom-8 left-3 text-xs text-white/30 font-mono">Eastlands</div>
        <div className="absolute bottom-8 right-8 text-xs text-white/30 font-mono">Southlands</div>

        {/* Driver Markers */}
        {filteredDrivers?.map((driver, idx) => {
          const pos = MOCK_POSITIONS?.[idx % MOCK_POSITIONS?.length];
          const statusConfig = DRIVER_STATUSES?.[driver?.status] || DRIVER_STATUSES?.offline;
          const isSelected = selectedDriver?.id === driver?.id;
          const isHovered = hoveredDriver === driver?.id;

          return (
            <div
              key={driver?.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{ left: `${pos?.x}%`, top: `${pos?.y}%` }}
              onClick={() => onDriverSelect?.(driver)}
              onMouseEnter={() => setHoveredDriver(driver?.id)}
              onMouseLeave={() => setHoveredDriver(null)}
            >
              {/* Pulse ring for active drivers */}
              {driver?.status !== 'offline' && (
                <div
                  className="absolute inset-0 rounded-full animate-ping opacity-40"
                  style={{ backgroundColor: statusConfig?.color, transform: 'scale(2)' }}
                />
              )}

              {/* Driver dot */}
              <div
                className={`relative w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${
                  isSelected ? 'scale-125 border-accent shadow-lg' : 'border-white/50'
                }`}
                style={{ backgroundColor: statusConfig?.color }}
              >
                <Icon name="Car" size={14} color="white" />
              </div>

              {/* Tooltip */}
              {(isHovered || isSelected) && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-10 whitespace-nowrap">
                  <div className="bg-primary text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-accent/30">
                    <p className="font-semibold">{driver?.name}</p>
                    <p className="text-white/70">{driver?.vehicleType} • {driver?.licensePlate}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusConfig?.color }} />
                      <span className="capitalize">{statusConfig?.label}</span>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-primary rotate-45 mx-auto -mt-1 border-r border-b border-accent/30" />
                </div>
              )}
            </div>
          );
        })}

        {/* Legend */}
        <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center space-x-3">
          {Object.entries(DRIVER_STATUSES)?.filter(([k]) => k !== 'offline')?.map(([key, cfg]) => (
            <div key={key} className="flex items-center space-x-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg?.color }} />
              <span className="text-white/80 text-xs">{cfg?.label}</span>
            </div>
          ))}
        </div>

        {/* Live indicator */}
        <div className="absolute top-3 right-3 flex items-center space-x-1.5 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
          <span className="text-white/80 text-xs font-medium">LIVE</span>
        </div>
      </div>
    </div>
  );
};

export default FleetMapPanel;
